import { useState, FC, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import { CRMProgram } from 'src/types/crmProgram';
import CrmApi from 'src/api/CrmApi';
import { CRMContact } from 'src/types/crmContact';
import { ActivityFormState } from 'src/types/activityFormState';

interface Props {
  formState: ActivityFormState;
  onProgramsChange: (programs: CRMProgram[]) => void;
  onContactsChange: (contacts: CRMContact[]) => void;
}

const ActivityMeetingFields: FC<Props> = ({ formState, onContactsChange, onProgramsChange }) => {
  const masterProgramsList = useRef<CRMProgram[]>([]);
  const masterContactsList = useRef<CRMContact[]>([]);
  const [programsList, setProgramsList] = useState<CRMProgram[]>([]);
  const [contactsList, setContactsList] = useState<CRMContact[]>([]);

  useEffect(() => {
    CrmApi.getAllCrmPrograms().then((programs: CRMProgram[]) => {
      masterProgramsList.current = programs;
      setProgramsList([...programs]);
    });
  }, []);

  useEffect(() => {
    CrmApi.getCrmContactsByCustomer(formState.formActivity.customerNumber).then((contacts: CRMContact[]) => {
      masterContactsList.current = contacts;
      setContactsList([...contacts]);
    });
  }, [formState.formActivity.customerNumber]);

  const handleProgramsChange = (event: MultiSelectChangeEvent) => {
    const values = event.target.value as CRMProgram[];
    const lastItem = values[values.length - 1];

    if (lastItem) {
      values.pop();
      const sameItem: CRMProgram = values.find((value) => value.programName === lastItem.programName);
      const itemInMasterList: CRMProgram = masterProgramsList.current.find((value) => value.programName === lastItem.programName);

      if (itemInMasterList === undefined) {
        //add to master list so it shows as a dropdown option
        masterProgramsList.current.push(lastItem);
      }

      if (sameItem === undefined) {
        values.push(lastItem);
      }
    }
    onProgramsChange(values);
    setProgramsList([...masterProgramsList.current]);
  };

  const handleContactsChange = (event: MultiSelectChangeEvent) => {
    const values = event.target.value as CRMContact[];
    const lastItem = values[values.length - 1];

    if (lastItem) {
      values.pop();
      const sameItem: CRMContact = values.find((value) => value.contactName === lastItem.contactName);
      const itemInMasterList: CRMContact = masterContactsList.current.find((value) => value.contactName === lastItem.contactName);

      if (itemInMasterList === undefined) {
        //add to master list so it shows as a dropdown option
        masterContactsList.current.push(lastItem);
      }

      if (sameItem === undefined) {
        values.push(lastItem);
      }
    }
    onContactsChange(values);
    setContactsList([...masterContactsList.current]);
  };

  const handleProgramfilterChange = (event: MultiSelectFilterChangeEvent) => {
    const { value } = event.filter;
    if (!value) {
      setProgramsList([...masterProgramsList.current]);
      return;
    }
    const result = filterBy(masterProgramsList.current, { field: 'programName', operator: 'contains', value: value, ignoreCase: true });
    setProgramsList(result);
  };

  const handleContactfilterChange = (event: MultiSelectFilterChangeEvent) => {
    const { value } = event.filter;
    if (!value) {
      setContactsList([...masterContactsList.current]);
      return;
    }
    const result = filterBy(masterContactsList.current, { field: 'contactName', operator: 'contains', value: value, ignoreCase: true });
    setContactsList(result);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }} style={{ paddingTop: 0 }}>
        <MultiSelect
          label="Programs Pitched"
          data={programsList}
          textField="programName"
          dataItemKey="programId"
          filterable={true}
          onFilterChange={handleProgramfilterChange}
          onChange={handleProgramsChange}
          value={formState.formActivity.programs}
          allowCustom={true}
          style={{ width: '300px' }}
          placeholder="Select or create programs ..."
        />
        <MultiSelect
          label="Contacts"
          data={contactsList}
          textField="contactName"
          dataItemKey="contactId"
          filterable={true}
          onFilterChange={handleContactfilterChange}
          onChange={handleContactsChange}
          value={formState.formActivity.contacts}
          allowCustom={true}
          style={{ width: '300px' }}
          placeholder="Select or create contacts ..."
        />
      </Grid>
    </Grid>
  );
};

export default ActivityMeetingFields;
